import {
  EditPostMutationVariables,
  MentionedUsersFieldFragment,
  PostAttachmentType,
  PostListQuery,
} from '__generated__/graphql/legacy/graphql';
import TrackingEvent, { TrackingProperty } from 'constants/trackingEvents';
import { relativeFormattedDateTime } from 'helpers/dateHelpers';
import {
  getTotalNumberOfReplies,
  sanitizeAttachments,
} from 'helpers/postHelpers';
import useSpacesList from 'hooks/useSpacesList';
import { Space } from 'interfaces/space';
import { TFunction } from 'next-i18next';
import dynamic from 'next/dynamic';
import { Post as PostType } from 'types/post';
import { PostListUser } from 'types/user';
import {
  EVENT_POST_TYPES,
  NUMBER_OF_REACTIONS,
  SUCCESS_TOAST,
} from 'utils/constants';
import { rawId } from 'utils/rawId';
import { genericToastAlert } from 'utils/toastHelpers';
import { canDeletePost } from 'utils/userHelpers';

import React, { useState } from 'react';

import {
  ApolloQueryResult,
  OperationVariables,
  useMutation,
} from '@apollo/client';
import { Mutation } from '@apollo/client/react/components';

import { toaster } from '@thinkific/toga-react';

import { useCommunityContext } from 'components/CommunityContext';
import Post from 'components/Post/Post';
import PostForm from 'components/PostForm/PostForm';

import {
  DELETE_POST_MUTATION,
  EDIT_POST_MUTATION,
} from 'schema/Post/mutations';
import { Attachment } from 'schema/Post/types';
import { SECTION_QUERY } from 'schema/Section/queries';

interface PostContainerProps {
  content: string;
  isPostPage?: boolean;
  isReplyPanel?: boolean;
  mentionedUsers?: MentionedUsersFieldFragment | null;
  post: PostType;
  refetchPostList?: (
    variables?: Partial<OperationVariables>
  ) => Promise<ApolloQueryResult<PostListQuery>>;
  showSpace?: boolean;
  t: TFunction;
  goToPostListPage?(): void;
  author?: PostListUser | null;
  onPostChange?(): void;
  showActions?: boolean;
}

export const PostContainer = ({
  isReplyPanel,
  content,
  mentionedUsers,
  author,
  post,
  t,
  refetchPostList,
  isPostPage = false,
  goToPostListPage,
  showSpace = false,
  onPostChange,
  showActions = true,
}: PostContainerProps) => {
  const { community, currentUser, trackEvent } = useCommunityContext();
  const spacesList = useSpacesList();
  const canUserDeletePost = canDeletePost(currentUser, author);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editPost, { loading: editPostSubmitting }] = useMutation(
    EDIT_POST_MUTATION
  );

  const trackPostEvent = (event) =>
    trackEvent(event, {
      [TrackingProperty.ATTACHMENTS]: post.postAttachments?.map(
        (attachment) => attachment.attachmentType as PostAttachmentType
      ),
      [TrackingProperty.PINNED_POST]: !!post.pinnedAt,
      [TrackingProperty.POST_ID]: rawId(post.id),
      [TrackingProperty.POST_ID_ENCODED]: post.id,
      [TrackingProperty.POST_TYPE]: EVENT_POST_TYPES.POST,
    });

  // modal actions
  const handleDeletePost = (event) => {
    document.body.style.overflow = 'hidden';
    setIsOpen(true);
    if (event) event.stopPropagation();
  };

  const closeDeletePostModal = () => {
    document.body.style.overflow = 'visible';
    setIsOpen(false);
  };

  const handleEditPost = () => {
    setIsEditing(true);
  };

  const handleConfirmDelete = (mutation) => async () => {
    try {
      const { data: deletePostData, errors: deletePostError } = await mutation({
        variables: { id: post.id },
        refetchQueries: isPostPage ? [] : ['PostList', 'CommunitySpace'],
        update() {
          closeDeletePostModal();
        },
      });

      trackPostEvent(TrackingEvent.DELETE_POST);
      const userErrors = deletePostData?.deletePost?.userErrors;
      if (!userErrors?.length && !deletePostError?.length) {
        toaster.notice(t(SUCCESS_TOAST.DELETE_POST));
      } else {
        genericToastAlert();
      }

      onPostChange?.();

      if (isPostPage) {
        goToPostListPage?.();
      }
    } catch {
      genericToastAlert();
    }
  };

  const renderConfirmDeleteModal = (mutate, loading) => {
    const ConfirmDeleteModal = dynamic(() =>
      import('components/ConfirmDeleteModal')
    );

    return (
      <span>
        <ConfirmDeleteModal
          closeAction={closeDeletePostModal}
          confirmDeleteBody={t(
            'components-post-confirm_delete_post',
            'Are you sure you want to delete this post? You cannot undo this action.'
          )}
          confirmDeleteTitle={t('Delete post')}
          deleteAction={handleConfirmDelete(mutate)}
          isConfirmModalOpen={isOpen}
          isLoading={loading}
        />
      </span>
    );
  };

  const handleEditSubmit = async (data) => {
    try {
      const {
        title,
        content: formContent,
        body,
        attachments,
        mentionedUserIds,
      } = data;

      const variables: EditPostMutationVariables = {
        input: {
          id: post.id,
          communitySpaceId: data.spaceId,
          title,
          content: formContent,
          body,
          attachments: sanitizeAttachments(attachments),
        },
        numberOfReactions: NUMBER_OF_REACTIONS,
      };

      if (mentionedUserIds?.length) {
        variables.input.mentionedUserIds = mentionedUserIds;
      }

      const { data: editPostData, errors: editPostError } = await editPost({
        variables,
        refetchQueries: [
          'PostList',
          {
            query: SECTION_QUERY,
            variables: {
              communityId: community.id,
            },
          },
        ],
      });

      const userErrors = editPostData?.editPost?.userErrors;
      if (!userErrors?.length && !editPostError?.length) {
        toaster.notice(t(SUCCESS_TOAST.EDIT_POST));
        setIsEditing(false);
        trackPostEvent(TrackingEvent.EDIT_POST);
        onPostChange?.();
        // In edit case, we probably don't want to reset the form
      } else {
        genericToastAlert();
      }
    } catch (error) {
      genericToastAlert();
    }
  };

  const creationDateTime = relativeFormattedDateTime(
    post.createdAt,
    currentUser?.locale,
    t
  );

  const postProps = {
    author,
    content,
    handleDeletePost,
    handleEditPost,
    isPostDetailPage: isPostPage,
    isReplyPanel,
    mentionedUsers,
    post,
    postReactionCount: post.postReactions ? post.postReactions.totalCount : 0,
    refetchPostList,
    showActions,
    showSpace,
    t,
    time: creationDateTime || '',
    totalCommentCount: getTotalNumberOfReplies(post),
    trackPostEvent,
  };

  return (
    <>
      {isEditing ? (
        <PostForm
          cancelEdit={() => {
            setIsEditing(false);
          }}
          initialValues={{
            title: post.title || '',
            content,
            body: post.body,
            spaceId: post.communitySpace?.id || '',
            attachments: (post.postAttachments as Attachment[]) || [],
            mentionedUserIds:
              mentionedUsers?.edges?.map((user) => user?.node?.id || '') || [],
          }}
          isSubmitting={editPostSubmitting}
          selectedSpace={post.communitySpace as Space}
          spacesList={spacesList}
          t={t}
          isEdit
          showSpaceSelect
          onSubmit={handleEditSubmit}
        />
      ) : (
        <Post {...postProps} />
      )}

      {canUserDeletePost && isOpen && (
        <Mutation mutation={DELETE_POST_MUTATION}>
          {(mutate, { loading }) => renderConfirmDeleteModal(mutate, loading)}
        </Mutation>
      )}
    </>
  );
};

export default PostContainer;
